import { Box, Flex, Text } from '@chakra-ui/react';
import { useContext, useEffect, useState } from 'react';
import { BrowserView } from 'react-device-detect';
import { GameListItem } from '../component/gameComponents/GameListItem';
import {
    GameListSorting,
    SORT_GAMES,
    SORT_PLAYERS,
} from '../component/gameComponents/GameListSorting';
import { BrowserSidebar } from '../component/globalComponents/BrowserSidebar';
import { BrowserTopBar } from '../component/globalComponents/BrowserTopBar';
import { MobileViewPage } from '../component/globalComponents/MobileViewPage';
import PageDataContext from '../context/PageDataContext';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { usePageDataCache } from '../hooks/usePageDataCache';
import { getGameInfo, LocalGameInfo } from '../service/games';
import { getRoomsStats, RoomsStats } from '../service/lobby';

interface GameListInfo {
    gameInfo: LocalGameInfo;
    gameStats?: RoomsStats;
}

export const GamesListPage = () => {
    const { isLoadFromCache } = useContext(PageDataContext);
    const [gamesList, setGamesList] = usePageDataCache<GameListInfo[]>('gamesList', []);
    const [filteredList, setFilteredList] = usePageDataCache<GameListInfo[]>(
        'filteredGamesList',
        [],
    );
    const [searchValue, setSearchValue] = useState<string>('');
    const [sortValue, setSortValue] = useLocalStorage<string>('sortValue', SORT_GAMES);

    const [pageLoading, setPageLoading] = useState<boolean>(true);

    const updateSorting = (newValue: string) => {
        setSortValue(newValue);
    };

    const updateSearch = (newValue: string) => {
        setSearchValue(newValue);
    };

    useEffect(() => {
        const getGamesList = async () => {
            const gameInfo = await getGameInfo();

            let infoList: GameListInfo[] = [];
            for (let i = 0; i < gameInfo.length; i++) {
                const element = gameInfo[i];
                const roomStats = await getRoomsStats(gameInfo[i].id);
                infoList[i] = {
                    gameInfo: element,
                    gameStats: roomStats,
                };
            }
            setGamesList(infoList);
            setPageLoading(false);
        };

        if (!isLoadFromCache) {
            getGamesList();
        } else {
            setPageLoading(false);
        }
    }, []);

    useEffect(() => {
        let filteredGames: GameListInfo[] = [];
        searchValue === ''
            ? (filteredGames = gamesList)
            : (filteredGames = gamesList.filter((info) =>
                  info.gameInfo.name.toLowerCase().includes(searchValue.toLowerCase()),
              ));

        switch (sortValue) {
            case SORT_GAMES:
                filteredGames.sort((a, b) => {
                    if (a.gameStats === undefined || b.gameStats === undefined) {
                        return 0;
                    } else {
                        return a.gameStats?.totalSessions > b.gameStats?.totalSessions ? -1 : 1;
                    }
                });
                break;
            case SORT_PLAYERS:
                filteredGames.sort((a, b) => {
                    if (a.gameStats === undefined || b.gameStats === undefined) {
                        return 0;
                    } else {
                        return a.gameStats?.totalPlayers > b.gameStats?.totalPlayers ? -1 : 1;
                    }
                });
                break;
        }

        setFilteredList(filteredGames);
    }, [sortValue, searchValue, gamesList]);

    return (
        <>
            <BrowserView>
                <BrowserTopBar isLoading={pageLoading} loadingColor={'#feca3c'}>
                    <Box className={'sixes-title-browser'}>
                        <Text>SIXES</Text>
                    </Box>
                </BrowserTopBar>
                <BrowserSidebar />
                <Box className={'games-list browser-content'}>
                    <GameListSorting
                        sortValue={sortValue}
                        updateFilter={updateSorting}
                        updateSearch={updateSearch}
                    />
                    <Box className={'games-list'}>
                        <Flex className={'games-list-heading'}>ON STREAM</Flex>
                        <Flex className={'game-container'}>
                            {filteredList.map((game) => (
                                <GameListItem
                                    key={game.gameInfo.id}
                                    game={game.gameInfo}
                                    stats={game.gameStats}
                                />
                            ))}
                        </Flex>
                        <Flex className={'games-list-heading'}>IN VENUE</Flex>
                    </Box>
                </Box>
            </BrowserView>
            <MobileViewPage
                title="SIXES"
                titleBackgroundColor="#feca3c"
                titleColor="#000"
                isLoading={pageLoading}
            >
                <GameListSorting
                    sortValue={sortValue}
                    updateFilter={updateSorting}
                    updateSearch={updateSearch}
                />
                <Box className={'games-list'}>
                    <Flex className={'games-list-heading'}>ON STREAM</Flex>
                    <Flex className={'game-container-mobile'}>
                        {filteredList.map((game) => (
                            <GameListItem
                                key={game.gameInfo.id}
                                game={game.gameInfo}
                                stats={game.gameStats}
                            />
                        ))}
                    </Flex>
                    <Flex className={'games-list-heading'}>IN VENUE</Flex>
                </Box>
            </MobileViewPage>
        </>
    );
};
